import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui";
import { useContacts } from "@/api/hooks/useContacts";

interface ContactPickerProps {
  value: string;
  onChange: (contactId: string, contactName: string) => void;
  placeholder?: string;
}

/**
 * Minimal search-as-you-type contact picker — reuses the existing
 * useContacts infinite query (src/api/hooks/useContacts.ts) filtered by
 * `q`, same search param the Contacts page's own search box uses. No new
 * backend endpoint needed. Kept local to features/memberships since
 * nothing else needs this exact compact combobox shape yet.
 *
 * The input's displayed text is ALWAYS the local `search`/display-name
 * state, never the raw `value` (a contact UUID) — parents only pass
 * `value` back in to know when to reset it (e.g. a dialog's on-open
 * effect clearing all fields); reflecting an id string into a text input
 * would show the UUID itself, not a name.
 */
export function ContactPicker({ value, onChange, placeholder }: ContactPickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  // Parent cleared `value` (e.g. dialog reset on reopen) — clear our own
  // display text too, since this component doesn't unmount between opens.
  useEffect(() => {
    if (!value) setSearch("");
  }, [value]);

  const { data } = useContacts({ q: search, limit: 8 });
  const results = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  return (
    <div className="relative">
      <Input
        value={search}
        placeholder={placeholder ?? "Search contacts by name or email…"}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
          if (value) onChange("", "");
        }}
        onFocus={() => setOpen(true)}
        data-testid="contact-picker-input"
      />
      {open && search.trim() && results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-bg-card shadow-lg">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-bg-hover"
                onClick={() => {
                  onChange(c.id, c.name);
                  setSearch(c.name);
                  setOpen(false);
                }}
                data-testid={`contact-picker-option-${c.id}`}
              >
                <span className="font-medium text-text-primary">{c.name}</span>
                {c.email && <span className="ml-2 text-text-muted">{c.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
